/**
 * Modern Performance Optimization Utilities
 * Uses latest web APIs for optimal performance
 */
(function() {
  'use strict';

  /**
   * Dynamically injects a script tag with modern loading strategies
   * @param {string} src - URL of the script to load
   * @param {Object} [options] - Optional loading flags
   * @returns {Promise} Promise that resolves when script loads
   */
  function loadScript(src, options) {
    options = options || {};
    return new Promise(function(resolve, reject) {
      // Check if script already exists
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        resolve();
        return;
      }

      var script = document.createElement('script');
      script.src = src;

      if (options.defer) {
        script.defer = true;
      }
      if (options.async) {
        script.async = true;
      }

      script.onload = function() {
        resolve();
      };

      script.onerror = function() {
        reject(new Error('Failed to load script: ' + src));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Load scripts during idle time to avoid blocking main thread
   * @param {Array<string>} scripts - Array of script URLs to load
   */
  function loadScriptsIdle(scripts) {
    if (!scripts || scripts.length === 0) return;

    function loadNext() {
      if (scripts.length === 0) return;

      var src = scripts.shift();
      loadScript(src, { async: true }).catch(function(err) {
        // Silently fail for non-critical scripts
      });

      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadNext, { timeout: 2000 });
      } else {
        setTimeout(loadNext, 100);
      }
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadNext, { timeout: 2000 });
    } else {
      setTimeout(loadNext, 100);
    }
  }

  /**
   * Optimize long tasks by breaking them into smaller chunks
   * @param {Function} task - Task function to execute
   * @param {Array} args - Arguments to pass to task
   */
  function runTaskInChunks(task, args) {
    if ('scheduler' in window && 'postTask' in window.scheduler) {
      // Use modern Scheduler API if available
      window.scheduler.postTask(function() {
        task.apply(null, args);
      }, { priority: 'background' });
    } else if ('requestIdleCallback' in window) {
      requestIdleCallback(function() {
        task.apply(null, args);
      }, { timeout: 2000 });
    } else {
      setTimeout(function() {
        task.apply(null, args);
      }, 0);
    }
  }

  // Expose utilities globally
  window.KTPerformance = {
    loadScript: loadScript,
    loadScriptsIdle: loadScriptsIdle,
    runTaskInChunks: runTaskInChunks
  };

  // Delay loading of non-critical helpers until idle time
  if ('requestIdleCallback' in window) {
    requestIdleCallback(function() {
      // Non-critical scripts can be loaded here during idle time
    }, { timeout: 2000 });
  } else {
    window.addEventListener('load', function() {
      setTimeout(function() {
        // Fallback for browsers without requestIdleCallback
      }, 1000);
    });
  }
})();

